import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import ClientFocusSelector from '../../../../../../apps/aiApp/Assistant/common/ClientFocusSelector'
import { useEvalRunForm } from './EvalRunFormContext'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-600)',
    flexShrink: 0
  },
  settings: {
    flexShrink: 0,
    padding: '10px 12px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: '8px',
    rowGap: '8px'
  },
  sectionLabel: {
    gridColumn: '1 / span 2',
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '2px'
  },
  sectionDivider: {
    gridColumn: '1 / span 2',
    height: 0,
    margin: '2px 0 0',
    borderTop: '1px dashed var(--color-neutral-200)'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0
  },
  label: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  input: {
    width: '100%',
    padding: '4px 8px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  inputMono: {
    fontFamily: 'var(--font-mono, monospace)',
    textAlign: 'right'
  },
  promptWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '12px',
    minHeight: 0
  },
  promptLabelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '4px'
  },
  promptLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  promptCharCount: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)'
  },
  promptHelp: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)',
    marginBottom: '6px',
    lineHeight: 1.4
  },
  promptTextarea: {
    flex: 1,
    width: '100%',
    padding: '10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    boxSizing: 'border-box',
    resize: 'none',
    fontFamily: 'inherit',
    minHeight: '160px',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  focusRow: {
    gridColumn: '1 / span 2',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }
}))

function parseTemperature (raw, setTemperature) {
  if (raw === '') {
    setTemperature(null)
    return
  }
  const val = parseFloat(raw)
  if (!Number.isFinite(val)) return
  if (val < 0 || val > 1) return
  setTemperature(val)
}

function RunSetupPanel ({ skillOptions, modelOptions }) {
  const classes = useStyles()
  const form = useEvalRunForm()

  return (
    <div className={classes.root}>
      <div className={classes.header}>Run setup</div>

      <div className={classes.settings}>
        <div className={classes.sectionLabel}>Skill</div>
        <div className={classes.field}>
          <span className={classes.label}>Skill</span>
          <select
            className={classes.input}
            value={form.skillId}
            onChange={(e) => form.setSkillId(e.target.value)}
          >
            <option value=''>{'\u2014 none \u2014'}</option>
            {skillOptions.map(s => (
              <option key={s.skillId} value={s.skillId}>{s.skillName || s.skillId}</option>
            ))}
          </select>
        </div>
        <div className={classes.field}>
          <span className={classes.label}>Tag</span>
          <input
            className={`${classes.input} ${classes.inputMono}`}
            value={form.skillTag}
            onChange={(e) => form.setSkillTag(e.target.value)}
            placeholder='live'
          />
        </div>

        <div className={classes.sectionDivider} />
        <div className={classes.sectionLabel}>Assistant model</div>
        <div className={classes.field}>
          <span className={classes.label}>Model</span>
          <select
            className={classes.input}
            value={form.modelOverride}
            onChange={(e) => form.setModelOverride(e.target.value)}
          >
            <option value=''>Default</option>
            {modelOptions.map(m => (
              <option key={m.modelId} value={m.modelId}>{m.displayName || m.modelId}</option>
            ))}
          </select>
        </div>
        <div className={classes.field}>
          <span className={classes.label}>Temperature</span>
          <input
            type='number'
            min={0}
            max={1}
            step={0.1}
            className={`${classes.input} ${classes.inputMono}`}
            value={form.temperature ?? ''}
            placeholder='default'
            onChange={(e) => parseTemperature(e.target.value, form.setTemperature)}
          />
        </div>

        <div className={classes.sectionDivider} />
        <div className={classes.sectionLabel}>Focus</div>
        <div className={classes.focusRow}>
          <ClientFocusSelector
            value={form.focus}
            onChange={form.setFocus}
            size='sm'
          />
        </div>
      </div>

      <div className={classes.promptWrap}>
        <div className={classes.promptLabelRow}>
          <span className={classes.promptLabel}>User prompt</span>
          <span className={classes.promptCharCount}>{form.prompt.length} chars</span>
        </div>
        <div className={classes.promptHelp}>
          Sent to Denali AI for every sample. Each sample runs in its own fresh thread.
        </div>
        <textarea
          className={classes.promptTextarea}
          value={form.prompt}
          onChange={(e) => form.setPrompt(e.target.value)}
          placeholder='What do you want to know?'
        />
      </div>
    </div>
  )
}

RunSetupPanel.propTypes = {
  skillOptions: PropTypes.array.isRequired,
  modelOptions: PropTypes.array.isRequired
}

export default RunSetupPanel
